// Domain types for the Loyalty module.
// Wire shapes are documented in breadstack_backend/docs/task_specs/STORE_LOYALTY_API.md.
// Convention: list/dashboard payloads are mapped to camelCase domain types in the gateway;
// the `LoyaltyRules` document is round-tripped to/from the server unchanged (snake_case)
// because the server's serializer expects exact field names on PUT.

export type LoyaltyTier = 'starter' | 'silver' | 'gold' | 'platinum'
export type LoyaltyMemberStatus = 'active' | 'inactive'
export type LoyaltyLogType = 'earned' | 'redeemed'
export type LoyaltyLogActivity =
  | 'order_complete'
  | 'order_refund'
  | 'signup'
  | 'birthday'
  | 'referral'
  | 'redeem'

export type LoyaltyMemberSortBy =
  | 'id'
  | 'points_balance'
  | 'lifetime_earned_points'
  | 'lifetime_redeem_points'
  | 'loyalty_last_activity'

export type LoyaltySortDirection = 1 | -1

// ── Rules ─────────────────────────────────────────────────────────────────────
// Server keeps snake_case; PUT body must match the serializer exactly. We keep
// the same shape on the client so save/load round-trips cleanly.

export interface LoyaltyTierRule {
  rate: string
  min_lifetime_pts: number
}

export interface LoyaltyEarnRules {
  base_rate: string
  silver: LoyaltyTierRule
  gold: LoyaltyTierRule
  platinum: LoyaltyTierRule
  enable: boolean
}

export interface LoyaltyRedemptionRules {
  rate: string
  minimum_redemption: number
  max_redemption_per_time: number
  allow_partial_redemption: boolean
}

export interface LoyaltyBonusPoints {
  signup_bonus: number
  birthday_bonus: number
  referral_bonus: number
  double_points_events: boolean
  flat_points_per_order: number
  enable: boolean
}

export interface LoyaltyExpiryLimits {
  points_expiry: number
  expiry_warning: number
  enable_point_expiry: boolean
  points_on_discounted_items: boolean
}

export interface LoyaltyRules {
  earn: LoyaltyEarnRules
  redemption: LoyaltyRedemptionRules
  bonus_points: LoyaltyBonusPoints
  expiry_limits: LoyaltyExpiryLimits
}

// ── Members list ──────────────────────────────────────────────────────────────

export interface LoyaltyMember {
  // Optional — present when the backend includes `customer_id` on member rows.
  // Required to fetch per-member logs via `/stores/:storeId/loyalty/members/:customerId/logs`.
  customerId?: number
  // POS-system customer id. Distinct from the loyalty `customerId` — the
  // Customer detail page in the host app keys off this value because it's
  // what the legacy customer index uses (route: `/s/:storeId/customers/:id/view`).
  // Optional because pre-POS-linked accounts may not have one yet.
  posCustomerId?: string
  firstName: string
  lastName: string
  email: string
  loyaltyTier: LoyaltyTier | null
  pointsBalance: number
  lifetimeEarnedPoints: number
  lifetimeRedeemPoints: number
  loyaltyLastActivity: string | null
  status: LoyaltyMemberStatus | null
}

export interface LoyaltyMembersParams {
  page: number
  page_size: number
  search?: string
  loyalty_tier?: LoyaltyTier
  status?: LoyaltyMemberStatus
  sort_by?: LoyaltyMemberSortBy
  sort?: LoyaltySortDirection
}

export interface LoyaltyMembersResponse {
  items: LoyaltyMember[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

// ── Overview ──────────────────────────────────────────────────────────────────

export interface LoyaltyOverviewSummary {
  totalMembers: number
  newMembersThisMonth: number
  pointsInCirculation: number
  pointsInValue: number
  totalEarned: number
  totalEarnedChangePct: number
  totalRedeemed: number
  redemptionRatePct: number
}

export interface LoyaltyTierDistribution {
  tier: LoyaltyTier | 'unknown'
  count: number
  percentage: number
}

export interface LoyaltyTopEarner {
  firstName: string
  lastName: string
  email: string
  lifetimeEarnedPoints: number
}

export interface LoyaltyPointsActivity {
  year: number
  month: number
  earned: number
  redeemed: number
}

export interface LoyaltyRecentActivity {
  title: string
  points: number
  type: LoyaltyLogType
  activity: LoyaltyLogActivity
  datetime: string
  orderId?: number
  customerName: string
}

// Popular Coupons widget on the Overview tab. Confirmed wire contract
// (2026-05-21): backend ships the list under `top_coupons` (the field on the
// overview response — not "popular_coupons"). Each item carries a numeric id,
// a free-form `description` (used as the main label since it's the most
// human-readable field — the `code` is a programmatic slug), `code`,
// `used` (= redemption count), `revenue` (gross order revenue tied to the
// coupon), and `discount` (dollar value of the discount granted per
// redemption).
//
// `discountType` is no longer present on the wire — the endpoint only emits a
// dollar value, so the UI treats it as fixed-dollar implicitly.
//
// `icon` / `iconBg` remain optional decoration handles in case the backend
// later starts shipping them; today the component falls back to defaults.
export interface LoyaltyPopularCoupon {
  // Numeric coupon id on the wire — keep as a number for parity. The component
  // tolerates a missing id by falling back to the description for the React key.
  id?: number
  // Short programmatic coupon code (e.g. `redeem-4pts-gef8up`). Displayed as
  // the row's sub-line in monospaced form.
  code: string
  // User-facing description (e.g. "redeem 4 point to 2$"). Used as the main
  // row heading because it's more descriptive than `code`.
  description: string
  icon?: string
  iconBg?: string
  // Dollar value of the discount granted per redemption. The backend currently
  // only emits fixed-dollar discounts here; the UI renders as "$X off".
  discount: number
  // Times this coupon has been redeemed. Drives the right-hand "redemptions"
  // stat on the row.
  used: number
  // Gross order revenue tied to this coupon over the period. Surfaced as a
  // secondary stat under the redemption count.
  revenue: number
}

export interface LoyaltyOverview {
  summary: LoyaltyOverviewSummary
  tierDistribution: LoyaltyTierDistribution[]
  topEarners: LoyaltyTopEarner[]
  pointsActivity: LoyaltyPointsActivity[]
  recentActivity: LoyaltyRecentActivity[]
  popularCoupons: LoyaltyPopularCoupon[]
}

// ── Member logs ───────────────────────────────────────────────────────────────

export interface LoyaltyMemberLogsParams {
  page: number
  page_size: number
}

export interface LoyaltyMemberLogSummary {
  loyaltyTier: LoyaltyTier
  pointsBalance: number
  lifetimeEarnedPoints: number
  lifetimeRedeemPoints: number
}

export interface LoyaltyMemberLogEntry {
  customerId: number
  title: string
  points: number
  type: LoyaltyLogType
  activity: LoyaltyLogActivity
  datetime: string
  orderId?: number
}

export interface LoyaltyMemberLogsResponse {
  summary: LoyaltyMemberLogSummary
  items: LoyaltyMemberLogEntry[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

// ── Points Adjustment (Members tab) ──────────────────────────────────────────
// Admin-driven balance change applied to a single member from the member
// drawer / row action. Mirrors the `#adjustPointsModal` markup in the design
// reference. Wire contract:
//   PUT /stores/:storeId/loyalty/members/:customerId/adjust-points
//   { action: "add" | "subtract", amount: number, reason: string, note: string }
// Internal shape keeps `direction` + reason-slug; gateway translates on send.

export type LoyaltyAdjustDirection = 'add' | 'subtract'

export type LoyaltyAdjustReason =
  | 'goodwill_credit'
  | 'customer_service'
  | 'bonus'
  | 'correction'
  | 'promo_campaign'
  | 'other'

export const LOYALTY_ADJUST_REASON_LABEL: Record<LoyaltyAdjustReason, string> =
  {
    goodwill_credit: 'Goodwill credit',
    customer_service: 'Customer service',
    bonus: 'Bonus',
    correction: 'Correction',
    promo_campaign: 'Promo / Campaign',
    other: 'Other',
  }

export interface LoyaltyAdjustPointsRequest {
  direction: LoyaltyAdjustDirection
  amount: number
  reason: LoyaltyAdjustReason
  note?: string
}

export interface LoyaltyAdjustPointsResponse {
  customerId: number
  // Post-adjustment balance from the server. Optional because the assumed
  // endpoint may not return it on every code path; the UI falls back to the
  // locally-computed preview.
  pointsBalance?: number
  lifetimeEarnedPoints?: number
  lifetimeRedeemPoints?: number
}

// ── Activity (Activity tab) ───────────────────────────────────────────────────
// The Activity tab is a global log of point earns / point redemptions /
// coupon redemptions / admin adjustments / expiries across all members.
//
// Wire contract (confirmed 2026-05-21):
//   GET /stores/:storeId/loyalty/activities
//     ?page=&page_size=&tab=all|points|coupons&date_range=7|30|90|all&search=
// Response data shape (the `httpClient.json.*` unwrap leaves us with the
// inner `data` object):
//   {
//     summary: { total_events, total_value_redeemed,
//                point_activity_count, point_activity_value,
//                coupons_redeemed_count, coupons_redeemed_value },
//     items:   [{ store_id, customer_id, admin_id?, admin_name?,
//                 datetime, type, activity, title,
//                 points, points_balance, member_name }],
//     total_items, total_pages, page, page_size
//   }

export type LoyaltyActivityRange = '7' | '30' | '90' | 'all'

// The pill filter on the Activity tab. Sent to the server as the `tab` query
// param. The full design has three states (`all` / `points` / `coupons`); the
// Coupons pill is hidden in the current release (parent agent owns the
// Coupons surface separately) but the literal stays so the wire contract is
// stable when it returns.
export type LoyaltyActivityFilter = 'all' | 'points' | 'coupons'

// Top-level direction. `earned` = positive delta, `redeemed` = negative.
// Re-uses the existing log-type union so the icon/style mapping is shared
// with the per-member logs view.
export type LoyaltyActivityType = LoyaltyLogType

// Activity verb — what the user/system did. The backend currently emits a
// known set (`order_complete`, `manual_adjust`, …) but new verbs may appear,
// so the type stays open via `string` for forward compatibility while the
// icon picker still gets autocompletion for the known cases.
export type LoyaltyActivityKind =
  | 'order_complete'
  | 'order_refund'
  | 'signup'
  | 'birthday'
  | 'referral'
  | 'redeem'
  | 'manual_adjust'
  | 'coupon_redeem'
  // Points returned to the member when a previously-redeemed coupon is removed.
  // A credit (positive `points`) — mapped to a green refund style, not a purple
  // "redeemed" row. Wire pairs this `activity` with `type: 'refund_redeem'`.
  | 'redeem_refund'
  | (string & {})

export interface LoyaltyActivityEvent {
  // Stable React key — synthesised from datetime + customer_id + activity by
  // the gateway because the wire payload has no top-level id field.
  id: string
  // Owning store (passed through for completeness, not rendered).
  storeId?: string
  // Member identity.
  customerId?: number
  memberName: string
  // Admin attribution — only populated on `activity === 'manual_adjust'` rows
  // (and even there, `adminName` is sometimes absent on the second of a pair
  // of "+50 / -50" rows). Render defensively.
  adminId?: string
  adminName?: string
  // Classification.
  type: LoyaltyActivityType
  activity: LoyaltyActivityKind
  // Free-form description shown as the row sub-line.
  title: string
  // Signed point delta. Positive for earns and refund-of-redemption credits
  // (points returned when a redeemed coupon is removed); negative for
  // redemptions / expiries / manual subtractions.
  points: number
  // Post-event balance, useful for audit trails on manual_adjust rows.
  pointsBalance?: number
  // RFC 2822 timestamp from the backend (e.g. "Thu, 21 May 2026 06:56:44 GMT").
  // The row renderer's `formatLastActivity` handles both RFC 2822 and ISO.
  datetime: string
}

// Activity-tab KPI summary returned alongside `items` on the list endpoint.
// All four stat cards are derived from this — the summary respects the
// current range / tab / search filters server-side.
export interface LoyaltyActivitySummary {
  totalEvents: number
  // Total dollar value redeemed (points + coupons combined).
  totalValueRedeemed: number
  // Points-only stats.
  pointActivityCount: number
  pointActivityValue: number
  // Coupon-only stats.
  couponsRedeemedCount: number
  couponsRedeemedValue: number
}

export interface LoyaltyActivityEventsParams {
  range: LoyaltyActivityRange
  // Pill filter — sent to the server as `tab`. `all` is forwarded.
  filter?: LoyaltyActivityFilter
  // Search by member name or item text.
  search?: string
  page?: number
  page_size?: number
}

export interface LoyaltyActivityEventsResponse {
  summary: LoyaltyActivitySummary
  items: LoyaltyActivityEvent[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

// ── Membership Pricing (Settings tab) ─────────────────────────────────────────
// Per-location member-only discount %. Sourced from the legacy promotion
// settings endpoint (`/stores/:storeId/promotions_settings`) — the global
// `promotions_settings` field is round-tripped untouched so we don't disturb
// the existing favor_customer / favor_store / compounding / cumulative logic.

export type PromotionsSettingsMode =
  | 'favor_customer'
  | 'favor_store'
  | 'compounding'
  | 'cumulative'

export interface PromotionsMembershipPricingLocation {
  warehouse_id: string
  enabled: boolean
  discount_percent: number
}

export interface PromotionsMembershipPricing {
  locations: PromotionsMembershipPricingLocation[]
}

export interface PromotionsSettingsDocument {
  promotions_settings?: PromotionsSettingsMode | string
  membership_pricing?: PromotionsMembershipPricing
}

// ── Promotion list (Membership Pricing data source) ──────────────────────────
// The Membership Pricing card is driven by `/stores/:storeId/promotions` — one
// row per promotion record. Display fields (name, address, phone, POS) are
// derived from the first entry of `location_ids` via the pantry warehouse map.
// `status === 1` (ACTIVE) drives the Enabled toggle's read-out.

export const PROMOTION_STATUS_VALUE = {
  DRAFT: -1,
  WAITING: 0,
  ACTIVE: 1,
  ENDING: 2,
  ENDED: 3,
} as const

export type PromotionStatusValue =
  (typeof PROMOTION_STATUS_VALUE)[keyof typeof PROMOTION_STATUS_VALUE]

export interface PromotionAllProducts {
  // Stored on the backend as a decimal fraction (e.g. 0.2 = 20%) when
  // `discount_type === 'percentage'`. The Membership Pricing row exposes this
  // as an integer percent for the Discount % column.
  discountAmount: number
  discountType?: string
}

export interface Promotion {
  id: string
  name: string
  status: PromotionStatusValue
  locationIds: string[]
  allProducts: PromotionAllProducts | null
  startDate?: string
  endDate?: string
  // Untouched raw API record (snake_case). Carried through so the per-row
  // update PUT can echo every field the server already knows about — only
  // the changed properties get overridden. The shape is intentionally
  // loose (`Record<string, unknown>`) because the backend serializer emits
  // many fields we don't have first-class domain types for yet (color,
  // start_time, frequency, customer_type, brands, categories, products,
  // composite_product_data, etc.).
  raw?: Record<string, unknown>
}

export interface PromotionListResponse {
  items: Promotion[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

// ── Promotion create (Add Location flow) ─────────────────────────────────────
// The Membership Pricing → Add Location dialog mints a new "basic / all
// products / registered customer" promotion targeting exactly one location.
// Body shape mirrors the legacy `revertNewDraftToApi` mapper in
// `frontend/src/pages/Promotion/hooks/mapper.js`, with the membership-pricing
// defaults baked in (registered customer, all products, percentage discount,
// no end date, repeating indefinitely off).

export interface CreatePromotionInput {
  // POS id of the location to attach the promotion to. The backend stores
  // this in `location_ids`; we always send a single-element array.
  locationId: string
  // Promotion display name. By convention the Add Location dialog sets this
  // to the selected location's name verbatim.
  name: string
  // Integer 0..100. Converted to a decimal fraction (× 0.01) on the wire.
  discountPercent: number
}

// Per-row update used by the Save panel on the Membership Pricing table.
// The PUT endpoint expects a full record, so the caller passes the current
// raw promotion (snake_case wire shape) under `current`; the gateway spreads
// it as the base body and overrides only the fields that actually changed.
// No bulk-edit endpoint exists yet, so the Save flow loops one row at a time.
export interface UpdatePromotionInput {
  // Current raw API record echoed back to the server so untouched fields
  // (color, start_time, frequency, customer_type, brands, categories,
  // products, etc.) stay intact through the round-trip.
  current?: Record<string, unknown>
  // Numeric status code. The toggle maps user intent to the target status:
  //   off → on  ⇒ WAITING (0)
  //   on  → off ⇒ ENDING  (2)
  status?: PromotionStatusValue
  // Integer 0..100. Sent on the wire as a decimal fraction under
  // `all_products.discount_amount` with `discount_type: 'percentage'`.
  discountPercent?: number
}

// ── Membership Pricing — UI row shape ─────────────────────────────────────────
// One row per promotion. The first id in `locationIds` is looked up in the
// pantry warehouse map to fill in display fields — name / address / phone /
// POS pill — none of which are editable here (POS is owned by the Location
// module). Enabled mirrors `promotion.status === ACTIVE`. `discountPercent`
// is a placeholder until the API exposes a per-row discount value.

export interface MembershipPricingRow {
  promotionId: string
  promotionName: string
  warehouseId: string
  name: string
  address: string
  phone: string
  posConnected: boolean
  posLabel: string
  enabled: boolean
  discountPercent: number
}

// ── Apple Wallet Pass Config ─────────────────────────────────────────────────
// Wire shape matches breadstack_backend/docs/task_specs/APPLE_WALLET_PASS_CONFIG_API.md.
// Snake_case end-to-end so the POST body round-trips cleanly without a mapper.

export interface AppleWalletPassConfigInput {
  icon_url: string
  icon_2x_url: string
  icon_3x_url: string
  logo_url: string
  logo_2x_url: string
  logo_3x_url: string
  logo_text: string
  organization_name: string
  description: string
}

export interface AppleWalletPassConfig extends AppleWalletPassConfigInput {
  _id: string
  store_id: string
  _created: string
  _updated: string
}
